/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React from "react";
import { ButtonGroup } from "components/ButtonOld/ButtonGroup";
import { DashboardSettingsButton } from "gui/dashboard/settings";
import { FullDashboard } from "lib/entries/types";
import { EntryFavoritesButton } from "gui/entries/favorites";
import { ForkButton } from "./ForkButton";
import { Grid } from "gui/grid/grid";
import { Header } from "shared/Header/Header";
import { LoadingPage } from "gui/loading/page";
import { NotFoundError } from "lib/entries/errors";
import { NotFoundPage } from "pages/404";
import { Page } from "gui/page/page";
import { PageHead } from "gui/head/head";
import { PageTitleOwner } from "gui/pagetitle/pagetitle";
import { ParameterMerge } from "lib/parameters/types";
import { RelatedDashboards } from "gui/dashboard/related";
import { SessionContext } from "gui/session/session";
import { ShareButton } from "gui/share/share";
import { Spacer } from "gui/spacer/spacer";
import { Tags } from "gui/tags/tags";
import { WidgetsEntriesButton } from "gui/widgets/entries";
import { WidgetsMemo } from "gui/widgets/widgets";
import { WidgetsTextButton } from "gui/widgets/text";
import { callUpsertDashboard } from "gui/dashboard/graphql";
import { dashboardLink } from "lib/links/links";
import { removeOrphanParams } from "gui/widgets/graphql";
import { useDashboardParameters } from "lib/parameters/hooks";
import { useAnalytics } from "gui/analytics/analytics";
import { useErrorBoundary } from "lib/hooks/use-error-boundary";
import { useFullDashboard } from "lib/entries/hooks";
import { useHasEditPermission } from "lib/permissions/permissions";
import { RefreshDashboardButton } from "./RefreshDashboardButton";
import { ScheduleDashboardExecutionButton } from "./ScheduleDashboardExecutionButton";
import { ForkedDashboardCaption } from "./ForkedDashboardCaption";
import { Button } from "components/Button/Button";
import { Tooltip } from "components/Tooltip/Tooltip";

interface DashboardProps {
  urlParams: {
    user: string;
    dashboard: string;
    apiKey: string | undefined;
  };

  title?: string;

  preview: boolean;
  timestamp: string;
}

export const DashboardPage: React.FC<DashboardProps> = (props) => {
  const dashboard = useFullDashboard(
    props.urlParams.user,
    props.urlParams.dashboard,
    props.urlParams.apiKey
  );
  const screenshot = dashboardLink(
    props.urlParams.user,
    props.urlParams.dashboard,
    props.urlParams.apiKey
  );

  const { captureEvent } = useAnalytics();

  const refresh = React.useCallback(async () => {
    await dashboard.mutate();
  }, [dashboard.mutate]);

  React.useEffect(() => {
    if (dashboard.data?.id) {
      captureEvent("Dashboard Viewed", { dashboardId: dashboard.data.id });
    }
  }, [dashboard.data?.id]);

  if (dashboard.error instanceof NotFoundError) {
    return <NotFoundPage />;
  }

  if (dashboard.error) {
    throw dashboard.error;
  }

  if (!dashboard.data) {
    return <LoadingPage title={props.title} screenshot={screenshot} />;
  }

  return (
    <>
      <PageHead
        title={dashboard.data.name}
        preview={props.preview}
        screenshot={screenshot}
      />
      <Header />
      <main key={dashboard.data.slug}>
        <DashboardPageContent
          dashboard={dashboard.data}
          refresh={refresh}
          apiKey={props.urlParams.apiKey}
        />
        <Spacer size="lg" />
      </main>
    </>
  );
};

const DashboardPageContent: React.FC<{
  dashboard: FullDashboard;
  refresh: () => Promise<void>;
  apiKey: string | undefined;
}> = (props) => {
  const { session } = React.useContext(SessionContext);
  const [edit, setEdit] = React.useState(false);
  const [isRunModalOpen, setIsRunModalOpen] = React.useState(false);
  const setError = useErrorBoundary();
  const parameters = useDashboardParameters(props.dashboard);
  const isPopular = props.dashboard.trending_scores.score_4h > 0;

  // A callback that saves a dashboard on widget layout edits.
  const onLayout = React.useCallback(
    (dashboard: FullDashboard) => {
      if (session) {
        callUpsertDashboard(dashboard, session).catch(setError);
      }
    },
    [session]
  );

  // Call parameters.state.onApply to update the state and query string.
  const onApply = React.useCallback(() => {
    parameters.state.onApply();
  }, [parameters.state]);

  return (
    <>
      <Page>
        <Grid>
          <DashboardPageTitle
            isPopular={isPopular}
            dashboard={props.dashboard}
            edit={edit}
            setEdit={setEdit}
            refresh={props.refresh}
            parameters={parameters}
            apiKey={props.apiKey}
            isRunModalOpen={isRunModalOpen}
            setIsRunModalOpen={setIsRunModalOpen}
          />
          <WidgetsMemo
            dashboard={props.dashboard}
            refresh={props.refresh}
            apiKey={props.apiKey}
            parameters={parameters}
            onLayout={onLayout}
            onApplyParam={onApply}
            openRunModal={() => setIsRunModalOpen(true)}
            edit={edit}
            disabledParameters={session === undefined}
          />
        </Grid>
      </Page>
      {!edit && (
        <Page size="md" key={props.dashboard.slug}>
          <RelatedDashboards dashboard={props.dashboard} />
        </Page>
      )}
    </>
  );
};

const DashboardPageTitle: React.FC<{
  dashboard: FullDashboard;
  edit: boolean;
  setEdit: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: () => Promise<void>;
  parameters: ParameterMerge;
  apiKey: string | undefined;
  isRunModalOpen: boolean;
  setIsRunModalOpen: (open: boolean) => void;
  isPopular: boolean;
}> = (props) => {
  const canEdit = useHasEditPermission(props.dashboard.owner);
  const tags = props.dashboard.tags ?? [];

  const favoritesButton = !props.edit && (
    <EntryFavoritesButton entry={props.dashboard} />
  );

  const shareButton = props.dashboard.is_archived
    ? !props.edit && (
        <Tooltip
          position="below-center"
          label="Archived dashboards cannot be shared"
        >
          <Button size="M" theme="tertiary" disabled>
            Share
          </Button>
        </Tooltip>
      )
    : !props.edit && (
        <ShareButton
          label="Share"
          type="dashboard"
          dashboard={props.dashboard}
          owner={props.dashboard.owner}
        />
      );

  const forkButton = !props.edit && canEdit && (
    <ForkButton dashboard={props.dashboard} />
  );

  const scheduleDashboardExecutionButton = !props.edit && canEdit && (
    <ScheduleDashboardExecutionButton dashboard={props.dashboard} />
  );

  const refreshDashboardButton = !props.edit && (
    <RefreshDashboardButton
      dashboard={props.dashboard}
      parameters={props.parameters}
      apiKey={props.apiKey}
      isRunModalOpen={props.isRunModalOpen}
      setIsRunModalOpen={props.setIsRunModalOpen}
    />
  );

  return (
    <PageTitleOwner
      owner={props.dashboard.owner}
      title={props.dashboard.name}
      private={props.dashboard.is_private}
      isArchived={props.dashboard.is_archived}
      tags={<Tags slug="dashboards" tags={tags} />}
      isPopular={props.isPopular}
      caption={ForkedDashboardCaption({ dashboard: props.dashboard })}
    >
      <ButtonGroup>
        {favoritesButton}
        {scheduleDashboardExecutionButton}
        {refreshDashboardButton}
        {forkButton}
        {shareButton}
        <EditButton {...props} />
      </ButtonGroup>
    </PageTitleOwner>
  );
};

const EditButton: React.FC<{
  dashboard: FullDashboard;
  edit: boolean;
  setEdit: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: () => Promise<void>;
  parameters: ParameterMerge;
}> = (props) => {
  const { session } = React.useContext(SessionContext);
  const [loading, setLoading] = React.useState(false);
  const setError = useErrorBoundary();
  const canEdit = useHasEditPermission(props.dashboard.owner);

  const onEdit = () => {
    props.setEdit((prev) => !prev);
  };

  const onDone = async () => {
    try {
      setLoading(true);

      await props.refresh();
      props.setEdit(false);
      setLoading(false);

      if (session && props.parameters.state.defaults.length > 0) {
        await removeOrphanParams(props.dashboard, session);
      }
    } catch (err) {
      setError(err);
    }
  };

  if (!session || !canEdit) {
    return null;
  }

  if (!props.edit) {
    return (
      <Button theme="tertiary" size="M" onClick={onEdit}>
        Edit
      </Button>
    );
  }

  return (
    <>
      <DashboardSettingsButton
        refresh={props.refresh}
        dashboard={props.dashboard}
      />
      <WidgetsTextButton dashboard={props.dashboard} refresh={props.refresh} />
      <WidgetsEntriesButton item={props.dashboard} refresh={props.refresh} />
      <Button onClick={onDone} loading={loading} size="M" theme="primary">
        Done
      </Button>
    </>
  );
};
